import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { gateFailureReason } from "../util/gateFailureReason";

// #2801. Reopens (un-finals) a ride's frozen sheets so its outcome can be
// corrected (Set Outcome, or editing the sheet). A reason is required and is
// recorded by score-api on an append-only unfinalizeEvents entry. A failure
// shows score-api's own message (never a status code); with no usable body it
// falls back to one generic line. `onReopened(data)` gets the 200 body
// ({ classId, classPlaced, unfinalizedSheetIds }) so the caller can show the
// notice and refetch.
export default function ReopenRideDialog({ open, rideId, onClose, onReopened }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setError(null);
    }
  }, [open]);

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = unwrap(await scoreApi.rides.unfinalizeRide(rideId, { reason: reason.trim() }));
      await onReopened(data ?? {});
      onClose();
    } catch (err) {
      setError(gateFailureReason(err, t) ?? t("rideEditor.reopenError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} data-testid="reopen-ride-dialog">
      <DialogTitle>{t("rideEditor.reopenTitle")}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{t("rideEditor.reopenBody")}</DialogContentText>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          label={t("rideEditor.reopenReasonLabel")}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          inputProps={{ "data-testid": "reopen-ride-reason" }}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="reopen-ride-error">
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={busy} data-testid="reopen-ride-cancel">
          {t("rideEditor.reopenCancel")}
        </Button>
        <Button
          variant="contained"
          loading={busy}
          disabled={reason.trim() === ""}
          onClick={apply}
          data-testid="reopen-ride-apply"
        >
          {t("rideEditor.reopenApply")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
